import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FaCheckCircle, FaClock, FaWrench, FaChartLine } from "react-icons/fa";
import styles from "./Pages.module.css";

function ManagedVsBreakFix() {
  return (
    <section className={styles.pageSection}>
      <Helmet>
        <title>Managed IT vs. Break-Fix IT Support | Tharpe Industries</title>
        <meta
          name="description"
          content="Managed IT vs. break-fix support: which model fits your Shreveport-Bossier business? Honest comparison covering cost, reliability, and which makes financial sense."
        />
        <meta
          name="keywords"
          content="managed IT services, break-fix IT support, IT support models, monthly IT plan, on-demand IT support"
        />
        <link rel="canonical" href="https://tharpeindustries.com/managed-it-vs-break-fix" />
      </Helmet>

      <h2>Managed IT vs. Break-Fix: What's Right for Your Business?</h2>

      <p>
        There are two main ways small businesses handle IT. Understanding the difference can save you a lot of frustration, and money.
      </p>

      <h3>Break-Fix: Pay When Something Breaks</h3>
      <p>
        Break-fix is simple. Something breaks. You call a tech. They come out (or connect remotely), fix the problem, send you a bill. Then you're done until the next thing breaks.
      </p>
      <p>
        <strong>The appeal:</strong> No monthly bill when things are working fine. You only pay when you actually need help.
      </p>
      <p>
        <strong>The reality:</strong> You're reacting instead of preventing. Your tech doesn't know your setup, your history, or what matters to your business. Every time something breaks, the fix takes longer because they're starting from scratch. And if something breaks during your busy season, you're scrambling.
      </p>

      <h3>Managed IT: Monthly Plan, Proactive Maintenance</h3>
      <p>
        Managed IT is different. You pay a monthly fee. In exchange, someone is monitoring your systems, doing maintenance, keeping updates current, and watching for problems before they happen. You also get a dedicated contact who knows your setup.
      </p>
      <p>
        <strong>The appeal:</strong> Predictable cost. Fewer surprises. Someone who knows your systems. Problems get caught before they become disasters.
      </p>
      <p>
        <strong>The reality:</strong> It costs more per month if your business rarely has IT issues. But if something does break, the cost is zero, it's covered. You also get someone who remembers you, knows what you care about, and doesn't have to start from scratch every time.
      </p>

      <h3>Which One Is Right for You?</h3>

      <div style={{ marginBottom: "2rem" }}>
        <h4 style={{ marginBottom: "1rem", color: "var(--primary-dark)" }}>
          <FaWrench style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
          Break-Fix Works If:
        </h4>
        <ul style={{ marginLeft: "2rem" }}>
          <li>You rarely have IT issues (lucky you)</li>
          <li>You have 1–2 computers, max</li>
          <li>You can tolerate some downtime without major business impact</li>
          <li>Your IT needs are truly unpredictable and minimal</li>
        </ul>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h4 style={{ marginBottom: "1rem", color: "var(--primary-dark)" }}>
          <FaClock style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
          Managed IT Works If:
        </h4>
        <ul style={{ marginLeft: "2rem" }}>
          <li>You have 3 or more computers or devices</li>
          <li>Downtime costs you real money or frustration</li>
          <li>You want someone who knows your setup and remembers your situation</li>
          <li>You'd rather have predictable monthly costs than surprise service bills</li>
          <li>You want proactive maintenance instead of just reactive firefighting</li>
        </ul>
      </div>

      <h3>The Honest Take</h3>
      <p>
        Neither model is wrong. It depends on your business. But here's what I see in practice: Most small businesses think they're break-fix businesses until something goes wrong. Then they wish they'd had someone watching their back the whole time.
      </p>
      <p>
        If you're not sure which model fits you, the IT Health Check is a good way to find out. I'll look at your setup, talk through what matters to your business, and give you an honest assessment. No pressure to pick one model or the other, just clarity.
      </p>

      <div className={styles.relatedPages}>
        <p className={styles.relatedPagesHeading}>Related resources:</p>
        <div className={styles.relatedPageLinks}>
          <Link to="/managed-it-services">View Managed IT Plans</Link>
          <Link to="/it-health-check">Start with an IT Health Check, $149</Link>
          <Link to="/how-to-choose-it-support">How to Choose IT Support</Link>
        </div>
      </div>

      <div className={styles.ctaContainer}>
        <p><strong>Start with the $149 IT Health Check</strong></p>
        <p className={styles.emergencyText}>I'll review your setup and help you understand what model makes sense for your business.</p>
        <Link to="/it-health-check" className={styles.ctaButton}>Book the IT Health Check</Link>
      </div>

      <div className={styles.secondaryCta}>
        <p>Want to talk through which model fits you? <Link to="/contact-us">Send me a message</Link> and I'll help you figure it out.</p>
      </div>

      <div className={styles.resourceLinks}>
        <p style={{ fontSize: "0.9rem", color: "var(--neutral-600)", marginBottom: "0.5rem" }}>
          Related: <Link to="/ransomware-protection">Ransomware Protection Guide</Link> · <Link to="/how-to-choose-it-support">How to Choose an IT Provider</Link>
        </p>
      </div>
    </section>
  );
}

export default ManagedVsBreakFix;
